import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { testimonials } from '@/mocks/data';
import { organizationSchema, generateWebPageSchema, aboutFAQSchema } from '@/mocks/schema';

export default function AboutPage() {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitStatus('success');
      return;
    }
    const msg = formState.message.trim();
    if (msg.length > 500) {
      setErrorMsg('留言内容不能超过500个字符');
      setSubmitStatus('error');
      return;
    }
    setSubmitStatus('loading');
    setErrorMsg('');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9u0a678l97n3eas3f00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setFormState({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText || '提交失败，请稍后重试';
        setErrorMsg(serverMsg);
      }
    } catch {
      setSubmitStatus('error');
      setErrorMsg('网络异常，请稍后重试');
    }
  };

  return (
    <>
      <JsonLd data={organizationSchema as unknown as Record<string, unknown>} />
      <JsonLd data={generateWebPageSchema(
        '关于我们 - 姨母的诱惑 | 成人情感文学推荐平台',
        '了解「姨母的诱惑」的品牌故事、核心理念与读者口碑。专业成人情感文学推荐平台，为成熟读者甄选精品。',
        'https://guangzhousoap.cn/about'
      ) as unknown as Record<string, unknown>} />
      <JsonLd data={aboutFAQSchema as unknown as Record<string, unknown>} />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="pt-20 pb-14 md:pb-20">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>

            {/* Brand Story */}
            <section className="mb-16 md:mb-20">
              <div className="max-w-3xl">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
                  关于「姨母的诱惑」
                </h1>
                <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
                  <p>
                    <strong>「姨母的诱惑」</strong>是一个专注于成人情感文学精品推荐的阅读平台。我们诞生于2024年，由一群热爱情感文学、关注人性探索的编辑和创作者共同发起。我们的初心很简单——在浩如烟海的作品中，为成熟读者甄选那些真正值得花时间品读的精品。
                  </p>
                  <p>
                    我们相信，情感文学从来不仅仅是消遣。优秀的情感文学作品能够穿越世俗的迷雾，触及人性最深的真实。它让我们理解他人的处境，也让我们更深刻地认识自己。而「姨母」这一独特的文学形象，正是理解这一文学传统的最佳入口。
                  </p>
                  <p>
                    在这个平台上，你不仅能发现高质量的作品推荐，还能读到深入的作品解析、学习系统的创作技巧，以及与其他同好交流阅读心得。我们希望，「姨母的诱惑」不仅是一个推荐平台，更是一个让情感文学爱好者找到归属的精神家园。
                  </p>
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="mb-16 md:mb-20">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { num: '120+', label: '精选文章' },
                  { num: '50+', label: '推荐作品' },
                  { num: '15万+', label: '月度读者' },
                  { num: '98%', label: '好评率' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                      {stat.num}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Core Values */}
            <section className="mb-16 md:mb-20">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8 text-center">
                我们的理念
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    icon: 'ri-star-line',
                    title: '品质至上',
                    desc: '每一篇推荐文章都经过编辑团队的严格筛选和精心打磨。我们不追求数量，只在意质量。好文章值得被认真对待，也值得被更多人看见。',
                  },
                  {
                    icon: 'ri-eye-line',
                    title: '深度洞察',
                    desc: '我们不止步于表面的故事复述，而是深入挖掘作品背后的人性情感与文学价值。帮助读者建立起系统的文学鉴赏能力。',
                  },
                  {
                    icon: 'ri-user-heart-line',
                    title: '读者为本',
                    desc: '我们始终将读者的阅读体验和成长放在首位。每一篇文章的选题、结构、深度，都是从读者的实际需求出发来设计的。',
                  },
                ].map((item) => (
                  <div key={item.title} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">{item.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-16 md:mb-20">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8 text-center">
                读者口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-4">
                      <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                      <div>
                        <h4 className="text-sm font-medium text-foreground-900">{t.name}</h4>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                      <div className="ml-auto flex gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <div key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                            <i className="ri-star-fill text-xs"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed italic">
                      &ldquo;{t.content}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section id="contact">
              <div className="max-w-2xl mx-auto">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-3 text-center">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm text-center mb-8">
                  有任何问题、建议或合作意向，欢迎通过下方表单与我们取得联系
                </p>

                {submitStatus === 'success' ? (
                  <div className="text-center py-10 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">消息已发送</h3>
                    <p className="text-sm text-foreground-500">感谢你的留言，我们会尽快回复</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <div className="spam-field">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        你的名字 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={formState.name}
                        onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                        required
                        placeholder="请输入你的名字"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱地址 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                        required
                        placeholder="请输入你的邮箱"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                        <span className="text-foreground-400 font-normal ml-2">（500字以内）</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formState.message}
                        onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                        required
                        maxLength={500}
                        rows={5}
                        placeholder="请输入你的留言..."
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      ></textarea>
                      <div className="text-xs text-foreground-400 mt-1 text-right">
                        {formState.message.length}/500
                      </div>
                    </div>
                    {submitStatus === 'error' && errorMsg && (
                      <div className="text-sm text-primary-500 bg-primary-50 px-4 py-2 rounded-lg">
                        {errorMsg}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitStatus === 'loading'}
                      className="w-full px-6 py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {submitStatus === 'loading' ? '发送中...' : '发送留言'}
                    </button>
                  </form>
                )}

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-2">
                      <i className="ri-mail-line"></i>
                    </div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm text-foreground-800">contact@guangzhousoap.cn</p>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-2">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <p className="text-xs text-foreground-500">微信公众号</p>
                    <p className="text-sm text-foreground-800">姨母的诱惑</p>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-2">
                      <i className="ri-time-line"></i>
                    </div>
                    <p className="text-xs text-foreground-500">响应时间</p>
                    <p className="text-sm text-foreground-800">48小时内回复</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}